import { User, UserManager, WebStorageStateStore, Log } from 'oidc-client-ts';
import { config } from '../config/config';

const APP_CONFIG_STORAGE_KEY = 'app_config';
const APP_CONFIG_VERSION_KEY = 'app_config_version';

class AuthService {
  private userManager: UserManager;
  private processingCallback: boolean = false;
  private _loggingOut: boolean = false;

  // In-tab de-dupe for token renewal (see renewAccessToken).
  private renewInFlight: Promise<void> | null = null;

  // In-memory embed-token slot — never persisted to localStorage.
  // Third-party iframe storage partitioning (Safari/iOS) blocks access to any
  // localStorage set during a normal top-level session, so the embed route
  // passes the token directly via URL and we hold it only in module memory.
  private _embedToken: string | null = null;
  private _embedTokenExpiry: Date | null = null;

  constructor() {
    // Enable detailed logging in development
    if (process.env.NODE_ENV === 'development') {
      Log.setLogger(console);
      Log.setLevel(Log.DEBUG);
    }

    const oidcConfig = {
      authority: config.auth.authority,
      client_id: config.auth.clientId,
      redirect_uri: `${window.location.origin}/callback`,
      post_logout_redirect_uri: window.location.origin,
      response_type: 'code',
      scope: config.auth.scope,
      loadUserInfo: true,
      stateStore: new WebStorageStateStore({ store: window.localStorage }),
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      monitorSession: false,
      // Renewal is driven manually by renewAccessToken() with a cross-tab lock,
      // NOT by oidc-client-ts's per-tab timer — see the addAccessTokenExpiring
      // handler below. Leaving automaticSilentRenew on would make every open tab
      // redeem the same rotated refresh token at once and collide on the server.
      automaticSilentRenew: false,
      metadata: {
        issuer: config.auth.authority,
        authorization_endpoint: `${config.auth.authority}connect/authorize`,
        token_endpoint: `${config.auth.authority}connect/token`,
        userinfo_endpoint: `${config.auth.authority}connect/userinfo`,
        end_session_endpoint: `${config.auth.authority}connect/logout`,
      }
    };

    this.userManager = new UserManager(oidcConfig);

    this.userManager.events.addUserLoaded((user) => {
      //console.log('User loaded:', user);
    });

    this.userManager.events.addUserUnloaded(() => {
      //console.log('User unloaded');
      this.clearStoredConfig();
    });

    this.userManager.events.addSilentRenewError((error) => {
      console.error('Silent renew error:', error);
    });

    // Drive renewal ourselves (automaticSilentRenew is off) so it can be
    // serialized across tabs. Fires ~60s before the access token expires.
    this.userManager.events.addAccessTokenExpiring(() => {
      void this.renewAccessToken();
    });

    this.userManager.events.addUserSignedOut(() => {
      console.log('User signed out');
      this.clearStoredConfig();
    });
  }

  private clearStoredConfig() {
    localStorage.removeItem(APP_CONFIG_STORAGE_KEY);
    localStorage.removeItem(APP_CONFIG_VERSION_KEY);
    localStorage.removeItem('all-events');
    localStorage.removeItem('defaultStartLocation');
    localStorage.removeItem('defaultEndLocation');
    localStorage.removeItem('businessHours');
    this.clearTenantCookie();
  }

  private clearTenantCookie() {
    // Clear __tenant cookie on all possible paths and domain variations
    const domain = window.location.hostname;
    const cookieName = '__tenant';
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
    // Also clear for parent domain (e.g., .routemize.com)
    const parts = domain.split('.');
    if (parts.length > 2) {
      const parentDomain = '.' + parts.slice(-2).join('.');
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${parentDomain}`;
    }
  }

  /**
   * Extract the tenantid claim from a JWT access token.
   * Returns the tenant ID string, or null for host tenant (no tenant claim).
   */
  public getTenantIdFromToken(accessToken: string): string | null {
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      return payload.tenantid || null;
    } catch {
      return null;
    }
  }

  /**
   * Resolve the current tenant name from the subdomain.
   * Returns null for host tenant (admin, www, bare domain).
   */
  public getSubdomainTenant(): string | null {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    if (hostname.endsWith('.localhost')) {
      if (parts.length >= 2) {
        const subdomain = parts[0];
        if (subdomain && !['www', 'api', 'app', 'admin', 'partner', 'localhost'].includes(subdomain.toLowerCase())) {
          return subdomain;
        }
      }
    } else if (parts.length > 2) {
      const subdomain = parts[0];
      if (subdomain && !['www', 'api', 'app', 'admin', 'partner'].includes(subdomain.toLowerCase())) {
        return subdomain;
      }
    }

    return null;
  }

  public async getUser(): Promise<User | null> {
    return this.userManager.getUser();
  }

  public async login(tenantName?: string, forcePrompt?: boolean, returnUrl?: string): Promise<void> {
    try {
      console.log('Initiating login process...', tenantName ? `with tenant: ${tenantName}` : 'without tenant');
      await this.userManager.clearStaleState();

      // Prepare extra query params for tenant
      const extraQueryParams: Record<string, string> = {};
      if (tenantName) {
        extraQueryParams.__tenant = tenantName;
      }

      return await this.userManager.signinRedirect({
        extraQueryParams,
        // prompt=login forces the auth server to show the login form even if
        // there's an active session cookie (used after logout to prevent auto-login)
        ...(forcePrompt ? { prompt: 'login' } : {}),
        // Round-trip the originally requested URL through the OIDC `state` so the
        // callback can return the user to it (survives the iframe's partitioned
        // storage — the state id travels in the URL). Read back via user.state.
        ...(returnUrl ? { state: { returnUrl } } : {})
      });
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  public async loginWithGoogle(tenantName?: string): Promise<void> {
    try {
      console.log('Initiating Google login...', tenantName ? `with tenant: ${tenantName}` : 'without tenant');
      await this.userManager.clearStaleState();

      // Prepare extra query params for tenant and Google provider
      const extraQueryParams: Record<string, string> = {
        acr_values: 'idp:Google' // Request Google as identity provider
      };

      if (tenantName) {
        extraQueryParams.__tenant = tenantName;
      }

      return await this.userManager.signinRedirect({
        extraQueryParams
      });
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  }

  public async loginCallback(): Promise<User> {
    if (this.processingCallback) {
      console.log('Callback already being processed, returning...');
      throw new Error('Callback already in progress');
    }

    this.processingCallback = true;

    try {
      console.log('Processing login callback...');
      const user = await this.userManager.signinRedirectCallback();
      console.log('Login callback successful, user:', user?.profile?.sub);
      return user;
    } catch (error) {
      console.error('Login callback error:', error);
      throw error;
    } finally {
      this.processingCallback = false;
    }
  }

  public get isLoggingOut(): boolean {
    return this._loggingOut;
  }

  public async logout(): Promise<void> {
    this._loggingOut = true;
    sessionStorage.setItem('logout_in_progress', 'true');
    // Clear gate check caches so next login re-validates
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith('gate:')) sessionStorage.removeItem(key);
    }
    console.log('Initiating logout process...');
    await this.userManager.signoutRedirect();
  }

  /**
   * Cross-tab single-flight access-token renewal.
   *
   * oidc-client-ts runs its renewal timer independently in every open tab, and
   * OpenIddict rotates refresh tokens (single-use). With N tabs open, each tab's
   * timer fires at ~the same instant and each redeems the SAME refresh token
   * from shared localStorage — colliding on the server token store
   * (AbpDbConcurrencyException -> 500 on /connect/token) and churning tokens.
   *
   * We disable automaticSilentRenew and renew here instead, serialized across
   * tabs with the Web Locks API. oidc-client-ts always reloads the user from
   * storage (getUser/signinSilent -> _loadUser), so the tab that wins the lock
   * refreshes once and writes the new token to shared storage; every other tab
   * then re-reads it as fresh and skips. One refresh instead of N, no collision.
   */
  private renewAccessToken(): Promise<void> {
    if (this.renewInFlight) return this.renewInFlight; // in-tab de-dupe

    const renew = async (): Promise<void> => {
      // A sibling tab may have rotated the token while we waited for the lock.
      // Re-read shared storage; if it is no longer inside the expiring window,
      // it is fresh — reuse it rather than redeem an already-rotated token.
      const user = await this.userManager.getUser();
      const notifyWindow = this.userManager.settings.accessTokenExpiringNotificationTimeInSeconds;
      if (user && !user.expired && (user.expires_in ?? 0) > notifyWindow) {
        return;
      }
      try {
        await this.userManager.signinSilent();
      } catch (error) {
        console.error('Silent token renew failed:', error);
      }
    };

    const locks = navigator.locks;
    const run = locks?.request
      ? locks.request('routemize-oidc-token-renew', renew)
      : renew();

    this.renewInFlight = Promise.resolve(run)
      .then(() => undefined)
      .finally(() => {
        this.renewInFlight = null;
      });
    return this.renewInFlight;
  }

  public async getAccessToken(): Promise<string | null> {
    try {
      const user = await this.userManager.getUser();
      return user?.access_token || null;
    } catch (error) {
      console.error('Get access token error:', error);
      return null;
    }
  }

  /**
   * The token that represents the *effective* identity for API/hub calls.
   * Precedence: embed token -> impersonation token -> switched-account session
   * -> the signed-in user's OIDC access token.
   *
   * This MUST be the single source of truth for every authenticated channel —
   * REST (OpenAPI.TOKEN) and the SignalR hub connection — so they never
   * authenticate as different principals. Using getAccessToken() directly for
   * the hub caused the hub to connect as the host admin during impersonation
   * while REST ran as the impersonated user.
   */
  public async getEffectiveAccessToken(): Promise<string | null> {
    // Embed token takes priority when set and unexpired.  It lives only in
    // module memory (never localStorage) so it is invisible to any other tab
    // or session — normal login and active impersonation sessions are
    // completely unaffected when no embed token is present.
    if (this._embedToken && this._embedTokenExpiry && new Date() < this._embedTokenExpiry) {
      return this._embedToken;
    }
    const impersonationToken = localStorage.getItem('impersonation_token');
    if (impersonationToken) {
      return impersonationToken;
    }

    // Account switching: the person's own session in another account they own. Ranked BELOW
    // impersonation so that impersonating a team member from inside a switched account still
    // resolves to the impersonated principal. Read lazily to avoid an import cycle
    // (AccountSessionManager imports this service).
    const { accountSessionManager } = await import('../business/auth/AccountSessionManager');
    const accountToken = await accountSessionManager.getAccessToken();
    if (accountToken) {
      return accountToken;
    }

    return this.getAccessToken();
  }

  /**
   * Store a short-lived embed token for the iframe booking route.
   * Parses the JWT exp claim using the same base64url-decode approach as
   * ImpersonationManager.getTokenExpiry() so getEffectiveAccessToken() can
   * skip it once it lapses.  Token is held only in memory — never written to
   * localStorage or sessionStorage.
   */
  public setEmbedToken(token: string): void {
    this._embedToken = token;
    try {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        this._embedTokenExpiry = payload.exp ? new Date(payload.exp * 1000) : null;
      } else {
        this._embedTokenExpiry = null;
      }
    } catch {
      this._embedTokenExpiry = null;
    }
  }

  /**
   * Clears the in-memory embed token, e.g. after server-side verification
   * (EmbedTokenRoute calling /api/impersonation/current-tenant) determines
   * it doesn't actually authenticate. Prevents a known-bad token from being
   * handed out by getEffectiveAccessToken() to any other caller.
   */
  public clearEmbedToken(): void {
    this._embedToken = null;
    this._embedTokenExpiry = null;
  }

  /** True while an embed token is set and unexpired (same check getEffectiveAccessToken uses). */
  public hasEmbedToken(): boolean {
    return !!this._embedToken && !!this._embedTokenExpiry && new Date() < this._embedTokenExpiry;
  }

  public async isAuthenticated(): Promise<boolean> {
    try {
      const user = await this.userManager.getUser();
      return !!user && !user.expired;
    } catch (error) {
      console.error('Authentication check error:', error);
      return false;
    }
  }

  public getUserManager(): UserManager {
    return this.userManager;
  }
}

const authService = new AuthService();
export default authService;
